import React from 'react'
import TimelineLink from './timeline-link.component'
import TimezoneAwareTime from 'src/components/timezone-aware-time/timezone-aware-time.component'
import { CurecallTag, CurecallTooltip, ECurecallSize, LockIcon } from '@honestica/curecall-ui'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTranslation } from 'react-i18next'
import { useSession } from 'src/providers/auth.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TTimelineWorkflowItem } from '../timeline.type'

type WorkflowTimelineProps = {
  workflow: TTimelineWorkflowItem
  isLast: boolean
  isDemo: boolean
  handleClickWorkflow: (workflow: TTimelineWorkflowItem) => boolean
  index: number
  isExecutingWorkflow: boolean
}

const WorkflowTimeline = ({
  workflow,
  isLast,
  isDemo,
  handleClickWorkflow,
  index,
  isExecutingWorkflow,
}: WorkflowTimelineProps) => {
  const { t } = useTranslation()
  const { currentUser } = useSession()
  const { isFeatureActivated } = useCurrentProject()
  const { date, label, done, displayToday, hasMediaVariable } = workflow
  const isBlockedByFeatureFlag = hasMediaVariable && !isFeatureActivated('patient-documents')
  const isClickable = isDemo && !done && !isExecutingWorkflow && !isBlockedByFeatureFlag
  /**
   * Admin users can click on any timeline elements, but if the element was not
   * initially clickable, it will ask for confirmation (hence, double-clicking)
   */
  const isClickableUsingSecondClickForAdmin = !isClickable && !isBlockedByFeatureFlag && currentUser?.isAdmin

  const [isWaitingForSecondClick, setIsWaitingForSecondClick] = React.useState(false)

  function startSecondClickPhase() {
    setIsWaitingForSecondClick(true)
    setTimeout(() => setIsWaitingForSecondClick(false), 4000)
  }

  return (
    <>
      <TimelineLink done={done} />
      <div
        className="workflow"
        onClick={() => {
          if (isClickable || isWaitingForSecondClick) {
            handleClickWorkflow(workflow) && setIsWaitingForSecondClick(false)
            return
          }
          if (isClickableUsingSecondClickForAdmin) {
            startSecondClickPhase()
            return
          }
        }}
        style={{
          cursor: isClickable || isClickableUsingSecondClickForAdmin ? 'pointer' : 'default',
        }}
      >
        <div
          className={twMerge(
            'workflow__date',
            (done || isExecutingWorkflow) && 'workflow__date--muted',
            'c-w-max',
          )}
        >
          <TimezoneAwareTime date={date} formatter="dateWithHours" />
        </div>
        <div
          className={twMerge(
            'workflow__rounded',
            (done || isExecutingWorkflow) && 'workflow__rounded--muted',
            isBlockedByFeatureFlag && 'c-flex c-items-center c-justify-center c-w-4 c-h-4 c-p-0',
          )}
        >
          {isBlockedByFeatureFlag && (
            <CurecallTooltip
              tooltipPosition="bottom"
              content={t('components.patient.patient-file.body.timeline.workflow-blocked-feature-flag')}
            >
              <LockIcon className="c-size-2 c-text-primary" />
            </CurecallTooltip>
          )}
        </div>
        <CurecallTag
          className="c-absolute c-left-[40px]"
          size={ECurecallSize.SMALL}
          timeline-index={index}
          isWorkflow
          isDisabled={done}
        >
          {isWaitingForSecondClick ? t('common.actions.click-to-confirm') : label}
        </CurecallTag>
      </div>
      {!isLast && <TimelineLink done={done} displayToday={displayToday} />}
    </>
  )
}

export default WorkflowTimeline
